import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BackendAuthService } from './backend-auth.service';
import { RequestorBlockStateService, isRequestorBlockedError } from './requestor-block-state.service';

const READ_ONLY_METHODS = ['GET', 'HEAD', 'OPTIONS'];
const BLOCK_MANAGEMENT_PATH = '/api/admin/request-blocks';

@Injectable()
export class BackendAuthInterceptor implements HttpInterceptor {
  constructor(
    private backendAuth: BackendAuthService,
    private blockState: RequestorBlockStateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isBackendRequest = req.url.startsWith(environment.backend_base_url);

    if (!isBackendRequest) {
      return next.handle(req);
    }

    const isMutation = !READ_ONLY_METHODS.includes(req.method.toUpperCase());
    const isBlockManagement = req.url.startsWith(`${environment.backend_base_url}${BLOCK_MANAGEMENT_PATH}`);

    // Blocked users keep read access; block-management routes stay open so blocked admins can still use them.
    if (isMutation && !isBlockManagement && this.blockState.current) {
      return throwError(() => new HttpErrorResponse({
        status: 429,
        statusText: 'Too Many Requests',
        url: req.url,
        error: this.blockState.current
      }));
    }

    const request$ = req.headers.has('Authorization')
      ? next.handle(req)
      : from(this.backendAuth.getBackendToken()).pipe(
          switchMap(token => next.handle(req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          })))
        );

    return request$.pipe(
      catchError(error => {
        if (isRequestorBlockedError(error)) {
          this.blockState.markBlocked(error.error);
        }
        return throwError(() => error);
      })
    );
  }
}